/**
 * Dropsite Secure Upload — single-page composition + file upload.
 *
 * State machine:
 *   - On any user interaction (subject blur, message blur, notify change, file
 *     add) we ensure a draft transfer exists by POSTing /draft (idempotent).
 *     transferOid hydrates from the form's data-transfer-oid on init so JS
 *     state matches the persistent draft on reload / back-navigation.
 *   - Subject / message / notify are persisted directly onto the draft
 *     transfer record via PATCH /draft on field blur. The draft is the
 *     single source of truth — GET /transfer reads them back from the draft
 *     to pre-populate the form on reload.
 *   - Files upload sequentially. The pending list is client-state with inline
 *     per-row progress; the uploaded list is server-state refreshed via HTMX
 *     after each successful upload or delete.
 *   - Click Send -> lock the entire form, flush field values onto the draft,
 *     and wait for the queue to drain. POST /send returns the confirmation
 *     fragment which the JS swaps in place of #secure-upload-content.
 */

const DRAFT_ENDPOINT = '/drop/upload/transfer/draft';
const PATCH_ENDPOINT = '/drop/upload/transfer/draft';
const UPLOAD_STREAM_ENDPOINT = '/drop/upload/transfer/upload-stream';
const FILES_FRAGMENT_ENDPOINT = '/drop/upload/transfer/files-fragment';
const SEND_ENDPOINT = '/drop/upload/transfer/send';

const CHROME_LIMIT_BYTES = 10 * 1024 * 1024 * 1024;
const DEFAULT_LIMIT_BYTES = 4 * 1024 * 1024 * 1024;

function maxUploadBytes() {
    const ua = navigator.userAgent || '';
    const isChrome = /Chrome/i.test(ua) && !/Edg/i.test(ua) && !/OPR/i.test(ua);
    return isChrome ? CHROME_LIMIT_BYTES : DEFAULT_LIMIT_BYTES;
}

function escapeHtml(text) {
    const d = document.createElement('div');
    d.textContent = String(text);
    return d.innerHTML;
}

function formatSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + ' ' + units[i];
}

async function safeJson(resp) {
    try { return await resp.json(); } catch (e) { return {}; }
}

const UploadFlow = {
    formEl: null,
    transferOid: null,
    queue: [],
    isUploading: false,
    sendPending: false,
    formLocked: false,
    nextId: 1,

    init() {
        this.formEl = document.querySelector('[data-upload-form]');
        if (!this.formEl) return;

        // Hydrate transferOid from the server-rendered data attribute so JS
        // state lines up with the persistent draft on reload / back-navigation.
        // The draft itself is the source of truth for compose fields; this
        // pointer just lets ensureDraft short-circuit instead of hitting the
        // server every time.
        const oidAttr = (this.formEl.getAttribute('data-transfer-oid') || '').trim();
        this.transferOid = oidAttr || null;
        this.queue = [];
        this.isUploading = false;
        this.sendPending = false;
        this.formLocked = false;
        this.nextId = 1;

        // Message length cap (mirrors transfer.message.max.length on the
        // server). Read from the textarea so the Jinja template owns the
        // value; default to 2000 if the attribute is missing.
        const msgEl = this.formEl.querySelector('#msg');
        this.messageMaxLength = msgEl
            ? (parseInt(msgEl.getAttribute('data-max-length') || '2000', 10) || 2000)
            : 2000;

        this.bindFieldHandlers();
        this.bindMessageCounter();
        this.bindFilePicker();
        this.bindDropZone();
        this.bindSendButton();
        // Initial paint of the Send button so the over-limit state is
        // reflected even if the page renders pre-populated over the cap.
        this.updateSendButton();
    },

    // --------------------------------------------------------- Field handlers

    bindFieldHandlers() {
        const subj = this.formEl.querySelector('input[name="description"]');
        const msg = this.formEl.querySelector('textarea[name="msg"]');
        const notif = this.formEl.querySelector('input[name="delivNotif"]');

        if (subj) {
            subj.addEventListener('blur', () => {
                this.patchField({ description: subj.value });
            });
        }
        if (msg) {
            msg.addEventListener('blur', () => {
                this.patchField({ msg: msg.value });
            });
        }
        if (notif) {
            notif.addEventListener('change', () => {
                this.patchField({ delivNotif: notif.checked ? 'true' : 'false' });
            });
        }
    },

    // -------------------------------------------------- Message length counter

    bindMessageCounter() {
        const field = this.formEl.querySelector('#msg');
        if (!field) return;
        const onInput = () => this.refreshMessageCounter();
        field.removeEventListener('input', onInput);
        field.addEventListener('input', onInput);
        this.refreshMessageCounter();
    },

    isMessageOverLimit() {
        if (!this.formEl) return false;
        const field = this.formEl.querySelector('#msg');
        if (!field) return false;
        return field.value.length > (this.messageMaxLength || 2000);
    },

    refreshMessageCounter() {
        const field = this.formEl && this.formEl.querySelector('#msg');
        const counter = document.getElementById('msg-char-counter');
        if (!field || !counter) return;
        const max = this.messageMaxLength || 2000;
        counter.textContent = field.value.length + ' / ' + max + ' characters';
        const overLimit = field.value.length > max;
        counter.classList.toggle('text-red-600', overLimit);
        counter.classList.toggle('text-gray-600', !overLimit);
        // Keep the Send button's disabled state aligned with the limit.
        // updateSendButton consults isMessageOverLimit() so this same call
        // also covers the case where the counter changes after a queue update.
        this.updateSendButton();
    },

    async patchField(fields) {
        // Returns true on success, false if anything went wrong. Callers that
        // need to gate downstream behavior (e.g. onSend's pre-finalize flush)
        // can await + branch on the result.
        try {
            await this.ensureDraft();
        } catch (e) {
            return false; // ensureDraft already surfaced the error
        }
        const body = new URLSearchParams();
        for (const [k, v] of Object.entries(fields)) body.append(k, v);
        try {
            const resp = await fetch(PATCH_ENDPOINT, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body,
            });
            if (!resp.ok) {
                const data = await safeJson(resp);
                this.showError(data.error || 'Unable to save changes.');
                return false;
            }
            return true;
        } catch (e) {
            this.showError('Network error while saving changes.');
            return false;
        }
    },

    // ------------------------------------------------------- Draft creation

    async ensureDraft() {
        if (this.transferOid) return this.transferOid;
        const resp = await fetch(DRAFT_ENDPOINT, { method: 'POST' });
        // Same guard as finalizeSend: the auth gate answers with HX-Redirect,
        // which plain fetch() ignores. Without this the user gets a dead-end
        // error instead of being sent to sign in.
        const redirectUrl = resp.headers.get('HX-Redirect');
        if (redirectUrl) {
            window.location.assign(redirectUrl);
            throw new Error('redirecting');
        }
        const data = await safeJson(resp);
        if (!resp.ok || !data.success) {
            const msg = data.error || 'Unable to start upload.';
            this.showError(msg);
            throw new Error(msg);
        }
        this.transferOid = data.transfer_oid;
        return this.transferOid;
    },

    // ------------------------------------------------------------ File picker

    bindFilePicker() {
        const wire = (linkSel, inputSel) => {
            const link = this.formEl.querySelector(linkSel);
            const input = this.formEl.querySelector(inputSel);
            if (!link || !input) return;
            link.addEventListener('click', (e) => {
                e.preventDefault();
                if (this.formLocked) return;
                input.click();
            });
            input.addEventListener('change', (e) => {
                const files = Array.from(e.target.files || []);
                this.addFiles(files);
                e.target.value = '';
            });
        };
        wire('[data-upload-select-files]', '[data-upload-file-input]');
        wire('[data-upload-select-folder]', '[data-upload-folder-input]');
    },

    // -------------------------------------------------- Whole-form drag-drop

    bindDropZone() {
        // Drop target is the entire form so the user can drop anywhere; the
        // visible cue is the file-box border glowing.
        const formTarget = this.formEl;
        const filesBox = document.getElementById('files-box');

        const isFileDrag = (e) => e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');
        const highlight = (on) => {
            if (!filesBox) return;
            filesBox.classList.toggle('ring-2', on);
            filesBox.classList.toggle('ring-leapfile-gray', on);
            filesBox.classList.toggle('border-leapfile-gray', on);
        };

        ['dragenter', 'dragover'].forEach((name) => {
            formTarget.addEventListener(name, (e) => {
                if (this.formLocked) return;
                if (!isFileDrag(e)) return;
                e.preventDefault();
                e.stopPropagation();
                highlight(true);
            });
        });
        ['dragleave', 'dragend', 'drop'].forEach((name) => {
            formTarget.addEventListener(name, () => highlight(false));
        });
        formTarget.addEventListener('drop', async (e) => {
            if (this.formLocked) return;
            if (!isFileDrag(e)) return;
            e.preventDefault();
            e.stopPropagation();
            const files = await this.collectFiles(e.dataTransfer);
            this.addFiles(files);
        });
    },

    async collectFiles(dataTransfer) {
        if (dataTransfer.items) {
            const entries = [];
            for (let i = 0; i < dataTransfer.items.length; i++) {
                const item = dataTransfer.items[i];
                if (item.kind === 'file') {
                    const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
                    if (entry) entries.push(entry);
                }
            }
            const files = [];
            for (const entry of entries) {
                await this.traverseEntry(entry, '', files);
            }
            return files;
        }
        return Array.from(dataTransfer.files || []);
    },

    async traverseEntry(item, path, files) {
        if (item.isFile) {
            const file = await new Promise((res, rej) => item.file(res, rej));
            const relPath = path + file.name;
            try {
                Object.defineProperty(file, 'webkitRelativePath', {
                    value: relPath, writable: false,
                });
            } catch (e) { /* property may be readonly */ }
            files.push(file);
        } else if (item.isDirectory) {
            const reader = item.createReader();
            const entries = await new Promise((res, rej) => reader.readEntries(res, rej));
            for (const e of entries) {
                await this.traverseEntry(e, path + item.name + '/', files);
            }
        }
    },

    // -------------------------------------------------------- Add to queue

    // Sum of file.size across rows already attached to the draft on the server
    // (read from data-uploaded-bytes on the server-rendered uploaded-files
    // list). Pending + new files are summed live below.
    uploadedBytesAttached() {
        let total = 0;
        document.querySelectorAll('#uploaded-files [data-uploaded-bytes]').forEach((el) => {
            const n = parseInt(el.getAttribute('data-uploaded-bytes') || '0', 10);
            if (!Number.isNaN(n)) total += n;
        });
        return total;
    },

    async addFiles(files) {
        if (this.formLocked) return;
        if (!files || files.length === 0) return;

        const maxBytes = maxUploadBytes();

        // Per-file cap (legacy parity).
        const oversized = [];
        const sizedOk = [];
        for (const file of files) {
            if (file.size > maxBytes) {
                oversized.push(file.name);
            } else {
                sizedOk.push(file);
            }
        }
        if (oversized.length) {
            this.showError(`File too large: ${oversized.join(', ')}`);
        }

        // Cumulative cap: already-attached + currently-queued + about-to-be-added.
        const queuedBytes = this.queue
            .filter((i) => i.status !== 'failed')
            .reduce((sum, i) => sum + (i.file.size || 0), 0);
        let runningTotal = this.uploadedBytesAttached() + queuedBytes;
        const accepted = [];
        const overCap = [];
        for (const file of sizedOk) {
            if (runningTotal + file.size > maxBytes) {
                overCap.push(file.name);
            } else {
                runningTotal += file.size;
                accepted.push(file);
            }
        }
        if (overCap.length) {
            this.showError(
                `Total upload size would exceed the limit; skipped: ${overCap.join(', ')}`
            );
        }
        if (accepted.length === 0) return;

        for (const file of accepted) {
            const id = String(this.nextId++);
            const item = { id, file, status: 'queued', uploadedBytes: 0, xhr: null };
            this.queue.push(item);
            this.renderRow(item);
        }

        try {
            await this.ensureDraft();
        } catch (e) {
            for (const item of this.queue) {
                if (item.status === 'queued') {
                    item.status = 'failed';
                    item.errorText = 'Could not start upload';
                    this.renderRow(item);
                }
            }
            return;
        }

        this.processQueue();
    },

    // -------------------------------------------------------- Sequential queue

    async processQueue() {
        if (this.isUploading) return;
        const next = this.queue.find((i) => i.status === 'queued');
        if (!next) {
            // Queue drained.
            if (this.sendPending) {
                // Block finalize if any uploads failed during the wait. The
                // user must remove or retry those before send proceeds.
                if (this.queue.some((i) => i.status === 'failed')) {
                    this.showError('One or more files failed to upload. Remove them or retry before sending.');
                    this.sendPending = false;
                    this.formLocked = false;
                    this.enableForm();
                    this.updateSendButton();
                    return;
                }
                this.finalizeSend();
            }
            this.updateSendButton();
            return;
        }

        this.isUploading = true;
        next.status = 'uploading';
        this.renderRow(next);
        this.updateSendButton();

        try {
            await this.uploadFile(next);
            // Awaited: overlapping list GETs can land out of order and repaint stale (#597).
            await this.refreshUploadedList();
            this.removeRow(next);
        } catch (err) {
            if (err && err.message === 'cancelled') {
                this.removeRow(next);
            } else {
                next.status = 'failed';
                next.errorText = (err && err.message) || 'Upload failed';
                this.renderRow(next);
            }
        } finally {
            this.isUploading = false;
            this.processQueue();
        }
    },

    uploadFile(item) {
        const file = item.file;
        const fileName = file.webkitRelativePath || file.name;
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            item.xhr = xhr;
            xhr.open('POST', UPLOAD_STREAM_ENDPOINT, true);
            xhr.setRequestHeader('Dropsite-File-Name', encodeURIComponent(fileName));
            xhr.setRequestHeader('Dropsite-File-Size', String(file.size));
            // No Dropsite-Last-File: never let upload-stream auto-finalize. The
            // new flow finalizes via POST /send.
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');

            // Stall watchdog (#687). xhr.timeout bounds the whole request, so any
            // value short enough to catch a hang also kills slow multi-GB uploads.
            // Watch for absence of movement instead.
            item.stalled = false;
            let watchdog = null;
            const armWatchdog = (ms) => {
                clearTimeout(watchdog);
                watchdog = setTimeout(() => {
                    item.stalled = true;
                    xhr.abort();
                }, ms);
            };

            xhr.upload.onprogress = (ev) => {
                armWatchdog(90000);
                if (ev.lengthComputable) {
                    item.uploadedBytes = ev.loaded;
                    this.renderRow(item);
                }
            };
            // Body fully sent: no more progress events will come, so switch to
            // bounding the wait for the response. Generous — the S3 write on the
            // server side retries 3x at a 120s read timeout.
            xhr.upload.onload = () => armWatchdog(180000);
            xhr.onload = () => {
                item.xhr = null;
                clearTimeout(watchdog);
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const data = JSON.parse(xhr.responseText);
                        if (data.success) return resolve();
                        return reject(new Error('Upload failed'));
                    } catch (e) {
                        return reject(new Error('Upload failed'));
                    }
                }
                // Only 403 rejections carry customer-appropriate text; other statuses are internal.
                let msg = 'Upload failed';
                if (xhr.status === 403) {
                    try {
                        const data = JSON.parse(xhr.responseText);
                        if (data.error) msg = data.error;
                    } catch (e) { /* ignore */ }
                }
                reject(new Error(msg));
            };
            xhr.onerror = () => {
                item.xhr = null;
                clearTimeout(watchdog);
                reject(new Error('Could not reach the server.'));
            };
            xhr.onabort = () => {
                item.xhr = null;
                clearTimeout(watchdog);
                // The watchdog aborts too, so tell a stall apart from the user's X.
                reject(new Error(item.stalled ? 'The server stopped responding.' : 'cancelled'));
            };
            armWatchdog(90000);
            xhr.send(file);
        });
    },

    // -------------------------------------------------- Pending row rendering

    renderRow(item) {
        const list = document.getElementById('pending-files');
        if (!list) return;
        let row = list.querySelector(`[data-pending-id="${item.id}"]`);
        if (!row) {
            row = document.createElement('li');
            row.setAttribute('data-pending-id', item.id);
            list.appendChild(row);
        }
        row.className = 'flex items-center gap-3 text-sm py-1 px-2';

        const fileName = item.file.webkitRelativePath || item.file.name;
        const pct = item.file.size > 0
            ? Math.min(100, Math.floor((item.uploadedBytes / item.file.size) * 100))
            : 0;
        const sizeText = formatSize(item.file.size);

        let statusHtml = '';
        if (item.status === 'queued') {
            statusHtml = '<span class="text-xs text-gray-500 whitespace-nowrap">Queued</span>';
        } else if (item.status === 'uploading') {
            statusHtml = `
              <div class="w-24 bg-gray-200 rounded-full h-2 overflow-hidden">
                <div class="bg-leapfile-gray h-2 rounded-full transition-all" style="width: ${pct}%"></div>
              </div>
              <span class="text-xs text-gray-600 whitespace-nowrap">${pct}%</span>
            `;
        } else if (item.status === 'failed') {
            statusHtml = `
              <button type="button"
                      class="text-xs font-semibold text-leapfile-gray underline hover:text-black cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      data-pending-retry="${item.id}">Retry</button>
            `;
        }

        // Failed rows trade the size for "Failed: <reason>" — inline, since tooltips
        // are unreachable on touch. No truncate: hiding the text is the bug.
        const detailHtml = item.status === 'failed'
            ? `<span class="text-xs text-red-600 flex-1 min-w-0"><span class="font-semibold">Failed:</span> ${escapeHtml(item.errorText || 'Upload failed')}</span>`
            : `<span class="text-xs text-gray-500 whitespace-nowrap">${sizeText}</span>`;

        row.innerHTML = `
          <span class="flex-1 truncate" title="${escapeHtml(fileName)}">${escapeHtml(fileName)}</span>
          ${detailHtml}
          ${statusHtml}
          <button type="button"
                  class="text-gray-500 hover:text-black hover:font-bold cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-pending-remove="${item.id}"
                  aria-label="Remove ${escapeHtml(fileName)}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        `;

        const removeBtn = row.querySelector(`[data-pending-remove="${item.id}"]`);
        if (removeBtn) {
            removeBtn.disabled = this.formLocked;
            removeBtn.onclick = () => this.removePending(item.id);
        }

        const retryBtn = row.querySelector(`[data-pending-retry="${item.id}"]`);
        if (retryBtn) {
            retryBtn.disabled = this.formLocked;
            retryBtn.onclick = () => {
                item.status = 'queued';
                item.uploadedBytes = 0;
                item.errorText = null;
                this.renderRow(item);
                this.updateSendButton();
                this.processQueue();
            };
        }
    },

    removeRow(item) {
        const row = document.querySelector(`[data-pending-id="${item.id}"]`);
        if (row) row.remove();
        this.queue = this.queue.filter((i) => i.id !== item.id);
    },

    removePending(id) {
        if (this.formLocked) return;
        const item = this.queue.find((i) => i.id === id);
        if (!item) return;
        if (item.xhr) {
            item.xhr.abort(); // queue processor handles removal in onabort
        } else {
            this.removeRow(item);
        }
        this.updateSendButton();
    },

    // ------------------------------------------- Uploaded list (server-state)

    async refreshUploadedList() {
        const target = document.getElementById('uploaded-files');
        if (!target) {
            console.error('[secure-upload] refresh skipped: #uploaded-files is not in the DOM');
            return;
        }
        // Fetched and assigned here rather than through htmx.ajax(): that swap
        // could fail to land without rejecting, so a stale list read as success.
        let html;
        try {
            const resp = await fetch(FILES_FRAGMENT_ENDPOINT, { cache: 'no-store' });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            html = await resp.text();
        } catch (e) {
            console.error('[secure-upload] uploaded-file list refresh failed:', e);
            this.showError('Could not refresh the file list. Reload the page before sending.');
            return;
        }

        const holder = document.createElement('div');
        holder.innerHTML = html;
        const incoming = holder.firstElementChild;
        if (!incoming || incoming.id !== 'uploaded-files') {
            console.error('[secure-upload] unexpected uploaded-file fragment:', html.slice(0, 200));
            this.showError('Could not refresh the file list. Reload the page before sending.');
            return;
        }
        target.replaceWith(incoming);

        // Each row carries hx-delete; without this the remove buttons are inert.
        if (window.htmx) window.htmx.process(incoming);
        else console.error('[secure-upload] htmx missing: uploaded-file remove buttons will not work');

        // Prove the repaint landed instead of assuming it.
        const expected = incoming.querySelectorAll('li').length;
        const actual = document.querySelectorAll('#uploaded-files li').length;
        if (actual !== expected) {
            console.error(`[secure-upload] uploaded-file list did not repaint: server sent ${expected} rows, DOM shows ${actual}`);
            this.showError('The file list below may be out of date. Reload the page before sending.');
        }
    },

    // ----------------------------------------------------------- Send button

    bindSendButton() {
        const btn = this.formEl.querySelector('[data-upload-send]');
        if (!btn) return;
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.onSend();
        });
    },

    async onSend() {
        if (this.formLocked) return;

        // Failed uploads must be removed or retried before send. Otherwise the
        // user could send a transfer that's missing files they thought were
        // attached.
        if (this.queue.some((i) => i.status === 'failed')) {
            this.showError('One or more files failed to upload. Remove them or retry before sending.');
            return;
        }

        // Lock first so a second click can't slip through while we await the
        // field flush below.
        this.formLocked = true;
        this.disableForm();

        // Flush the current values of subject / message / notify onto the
        // draft before finalize. patchField only fires on blur/change, so a
        // user who clicks Send while focused in a field would otherwise see
        // stale draft values used by the server-side validation. Abort the
        // send if the flush fails so finalize doesn't run with stale data.
        if (this.transferOid) {
            const subj = this.formEl.querySelector('input[name="description"]');
            const msg = this.formEl.querySelector('textarea[name="msg"]');
            const notif = this.formEl.querySelector('input[name="delivNotif"]');
            const fields = {};
            if (subj) fields.description = subj.value;
            if (msg) fields.msg = msg.value;
            if (notif) fields.delivNotif = notif.checked ? 'true' : 'false';
            const flushedOk = await this.patchField(fields);
            if (!flushedOk) {
                // patchField surfaced the error in #upload-error-alert; unlock
                // so the user can retry once the failure is addressed.
                this.formLocked = false;
                this.enableForm();
                this.updateSendButton();
                return;
            }
        }

        const stillWorking = this.isUploading
            || this.queue.some((i) => i.status === 'queued' || i.status === 'uploading');
        if (!stillWorking) {
            this.finalizeSend();
        } else {
            this.sendPending = true;
            this.updateSendButton();
        }
    },

    async finalizeSend() {
        try {
            const resp = await fetch(SEND_ENDPOINT, { method: 'POST' });
            // The guest-auth gate answers with HX-Redirect, which plain fetch()
            // ignores; follow it here instead of swapping its empty body in.
            const redirectUrl = resp.headers.get('HX-Redirect');
            if (redirectUrl) {
                window.location.assign(redirectUrl);
                return;
            }
            if (resp.redirected) {
                window.location.assign(resp.url);
                return;
            }
            const text = await resp.text();
            if (resp.ok && text.trim()) {
                // Server returned the confirmation fragment — swap it in place
                // of the upload content. No navigation, no page reload.
                const target = document.getElementById('secure-upload-content');
                if (target) {
                    target.outerHTML = text;
                } else {
                    // Fallback: shouldn't happen in normal flow.
                    document.body.innerHTML = text;
                }
                return;
            }
            // Validation/blocking error — show inline alert, unlock the form.
            // An ok-but-empty body is a failure too: never blank the form.
            const region = document.getElementById('transfer-form-alert');
            if (region) {
                region.innerHTML = text.trim()
                    || '<div class="failAlertBox" role="alert"><p>Unable to send. Please try again.</p></div>';
            }
            this.formLocked = false;
            this.sendPending = false;
            this.enableForm();
            this.updateSendButton();
        } catch (e) {
            this.showError('Network error while sending.');
            this.formLocked = false;
            this.sendPending = false;
            this.enableForm();
            this.updateSendButton();
        }
    },

    updateSendButton() {
        const btn = this.formEl.querySelector('[data-upload-send]');
        if (!btn) return;
        const remaining = this.queue.filter((i) => i.status === 'queued' || i.status === 'uploading').length;
        if (this.sendPending && remaining > 0) {
            btn.disabled = true;
            btn.textContent = `Sending after uploads finish (${remaining} left)…`;
        } else if (this.sendPending) {
            btn.disabled = true;
            btn.textContent = 'Sending…';
        } else {
            // Keep Send disabled while the message is over the cap — otherwise
            // a queue update could re-enable the button and let the user
            // submit something the server will reject.
            btn.disabled = this.isMessageOverLimit();
            btn.textContent = 'Send';
        }
    },

    // ---------------------------------------------------------- Form locking

    disableForm() {
        this.formEl.querySelectorAll('input, textarea, select, button').forEach((el) => {
            if (el.matches('[data-upload-cancel]')) return; // Cancel stays usable
            if (el.matches('[data-upload-send]')) return;   // Send is managed separately
            el.disabled = true;
        });
    },

    enableForm() {
        this.formEl.querySelectorAll('input, textarea, select, button').forEach((el) => {
            el.disabled = false;
        });
    },

    // -------------------------------------------------------------- Helpers

    showError(message) {
        const region = document.getElementById('upload-error-alert');
        if (!region) return;
        region.innerHTML = `<div class="failAlertBox" role="alert"><p>${escapeHtml(message)}</p></div>`;
    },
};

export function initUploadFragment() {
    UploadFlow.init();
    document.addEventListener('htmx:afterSwap', (event) => {
        const tgt = event.target;
        if (!tgt) return;
        // Only re-init when the entire upload content area is replaced (e.g.,
        // user clicks "Not you?" to switch back to sender info, or sender info
        // returns the upload form). Internal swaps inside the form (like the
        // uploaded-files list refresh after each upload, or DELETE responses
        // replacing that list) MUST NOT trigger re-init — that would wipe
        // this.queue and this.transferOid mid-flight.
        if (tgt.id === 'secure-upload-content') {
            UploadFlow.init();
        }
    });
}
